import Header from "@/components/Header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { ROUTES } from "@/lib/routes";
import { 
  FileDown, 
  Smartphone, 
  MapPin, 
  Upload, 
  ArrowRight,
  ExternalLink,
  CheckCircle2
} from "lucide-react";

const GpxTracksGuide = () => {
  const recordingApps = [
    {
      name: "GPS Tracks",
      platform: "iOS",
      description: "Simple, free GPS tracking with easy GPX export. Our top recommendation for iPhone users.",
      free: true,
      link: "https://apps.apple.com/us/app/gps-tracks/id425589565",
    },
    {
      name: "Gaia GPS",
      platform: "iOS / Android",
      description: "Premium mapping with offline maps, perfect for remote areas without cell service.",
      free: false,
      link: "https://www.gaiagps.com/",
    },
    {
      name: "OSMAnd",
      platform: "Android",
      description: "Open-source navigation with GPX recording and offline maps. Great free option for Android.",
      free: true,
      link: "https://osmand.net/",
    },
    {
      name: "GPS Logger",
      platform: "Android",
      description: "Lightweight, battery-efficient GPS logging specifically designed for creating GPX files.",
      free: true,
      link: "https://gpslogger.app/",
    },
    {
      name: "onX Offroad",
      platform: "iOS / Android",
      description: "Popular off-road app with trail maps and route recording. Great for discovering trails.",
      free: false,
      link: "https://www.onxmaps.com/offroad",
    },
  ];

  const steps = [
    {
      number: 1,
      title: "Start Recording",
      description: "Open your GPS app and start a new track before you hit the trail.",
    },
    {
      number: 2,
      title: "Drive Your Route",
      description: "The app records your location as you drive, creating a breadcrumb trail.",
    },
    {
      number: 3,
      title: "Stop & Export",
      description: "When finished, stop recording and export the track as a GPX file.",
    },
    {
      number: 4,
      title: "Upload to Airing Down",
      description: "Upload your GPX file and we'll analyze your route automatically.",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="container max-w-4xl mx-auto px-4 py-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="mx-auto mb-6 w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <FileDown className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-4xl font-bold mb-4">What Are GPX Tracks?</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            GPX files are the standard format for recording GPS routes. They capture your exact path, 
            letting you log, share, and relive your off-road adventures.
          </p>
        </div>

        {/* What's in a GPX file */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" />
              What's Inside a GPX File?
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <span className="font-medium">Location Points</span>
                  <p className="text-sm text-muted-foreground">Latitude and longitude coordinates recorded at regular intervals</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <span className="font-medium">Timestamps</span>
                  <p className="text-sm text-muted-foreground">When each point was recorded, allowing us to calculate speed and duration</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <span className="font-medium">Elevation Data</span>
                  <p className="text-sm text-muted-foreground">Altitude at each point (when available) for elevation gain calculations</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <span className="font-medium">Waypoints</span>
                  <p className="text-sm text-muted-foreground">Named points of interest you mark along the way</p>
                </div>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* How to record */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-primary" />
              How to Record a GPX Track
            </CardTitle>
            <CardDescription>
              It's easier than you think! Follow these four simple steps.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-6">
              {steps.map((step) => (
                <div key={step.number} className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                    {step.number}
                  </div>
                  <div>
                    <h3 className="font-medium">{step.title}</h3>
                    <p className="text-sm text-muted-foreground">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recommended apps */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Recommended Recording Apps</CardTitle>
            <CardDescription>
              These apps make it easy to record and export GPX files from your phone or GPS device.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recordingApps.map((app) => (
                <a
                  key={app.name}
                  href={app.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 p-4 rounded-lg border hover:bg-muted/50 transition-colors"
                >
                  <div className="shrink-0 pt-0.5">
                    <Smartphone className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium">{app.name}</h3>
                      <Badge variant="secondary" className="text-xs">{app.platform}</Badge>
                      {app.free && <Badge variant="outline" className="text-xs border-green-500 text-green-600">Free</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{app.description}</p>
                  </div>
                  <ExternalLink className="w-4 h-4 text-muted-foreground shrink-0" />
                </a>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Pro tips */}
        <Card className="mb-8 bg-primary/5 border-primary/20">
          <CardHeader>
            <CardTitle>Pro Tips for Better Tracks</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold">•</span>
                <span><strong>Enable high accuracy mode</strong> - Uses more battery but records better data</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold">•</span>
                <span><strong>Keep your phone charged</strong> - Recording GPS can drain battery quickly</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold">•</span>
                <span><strong>Start recording before you leave pavement</strong> - Captures your full route</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold">•</span>
                <span><strong>Download offline maps</strong> - Works even without cell service</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary font-bold">•</span>
                <span><strong>Mark waypoints for interesting spots</strong> - They'll be included in your upload</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Ready to Log Your First Run?</h2>
          <p className="text-muted-foreground mb-6">
            Upload a GPX file from your latest adventure and we'll take care of the rest.
          </p>
          <div className="flex gap-4 justify-center">
            <Button variant="outline" size="lg" asChild>
              <Link to={ROUTES.explore}>
                <MapPin className="w-4 h-4 mr-2" />
                Explore Trails
              </Link>
            </Button>
            <Button variant="hero" size="lg" asChild>
              <Link to={ROUTES.uploadRun}>
                <Upload className="w-4 h-4 mr-2" />
                Upload GPX File
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GpxTracksGuide;
